import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CodeNameModel } from 'components/common/types';
import { DEFAULT_CRITERIA } from 'consts/targetCriteria';
import { getPublisherTargetCriteria } from 'store/publisher/selectors';
import { isDefaultCriteria } from 'utils/getPublisherDefaultCriteria';
import { UseTarget, UseTargetProps } from './TargetsPanel.types';

const useTarget = ({
  target,
  targetCriteria,
  measure,
  available,
  onValueChange,
  onPriorityChange,
  onGreenToleranceChange,
  onOrangeToleranceChange,
}: UseTargetProps): UseTarget => {
  const publisherCriteria = useSelector(getPublisherTargetCriteria);

  const getEffectiveCriteria = (): typeof DEFAULT_CRITERIA => {
    if (!targetCriteria) {
      return publisherCriteria;
    }

    if (isDefaultCriteria(targetCriteria)) {
      return publisherCriteria;
    }

    return targetCriteria;
  };

  const { priority, greenTolerance, orangeTolerance } = getEffectiveCriteria();

  const [value, setValue] = useState(target);
  const [priorityValue, setPriorityValue] = useState(priority);
  const [greenToleranceValue, setGreenToleranceValue] = useState(greenTolerance);
  const [orangeToleranceValue, setOrangeToleranceValue] = useState(orangeTolerance);

  const hasTargetValue = Boolean(value);
  const isChange =
    (target || 0) !== (value || 0) ||
    (hasTargetValue &&
      (priority !== priorityValue ||
        greenTolerance !== greenToleranceValue ||
        orangeTolerance !== orangeToleranceValue));

  const isToleranceError = greenToleranceValue > orangeToleranceValue;

  const handleValueChange = (targetValue: number): void => {
    setValue(targetValue);
    onValueChange?.(targetValue);
  };

  const handlePriorityChange = (targetPriority: CodeNameModel): void => {
    setPriorityValue(targetPriority);
    onPriorityChange?.(targetPriority);
  };

  const handleGreenToleranceChange = (targetGreenTolerance: number): void => {
    setGreenToleranceValue(targetGreenTolerance);
    onGreenToleranceChange?.(targetGreenTolerance);
  };

  const handleOrangeToleranceChange = (targetOrangeTolerance: number): void => {
    setOrangeToleranceValue(targetOrangeTolerance);
    onOrangeToleranceChange?.(targetOrangeTolerance);
  };

  return {
    value,
    priority: priorityValue,
    greenTolerance: greenToleranceValue,
    orangeTolerance: orangeToleranceValue,
    handleValueChange,
    handlePriorityChange,
    handleGreenToleranceChange,
    handleOrangeToleranceChange,
    allocated: measure?.allocated,
    feedback: measure?.status,
    available: available && !measure?.allocated ? available : undefined,
    isToleranceError,
    isChange,
  };
};

export default useTarget;
